import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { waLink } from "@/lib/api";
import Logo from "@/components/Logo";

const links = [
  { to: "/", label: "Home" },
  { to: "/guide", label: "Guide e diritti" },
  { to: "/iscrizione", label: "Iscriviti gratis" },
  { to: "/assistenza", label: "Assistenza" },
  { to: "/prenota", label: "Prenota" },
  { to: "/faq", label: "FAQ" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 bg-white/95 backdrop-blur transition-shadow duration-300 ${
        scrolled ? "shadow-md shadow-brand-blue/5" : ""
      } border-b border-slate-100`}
    >
      <div className="container-site flex items-center justify-between h-20">
        <Link to="/" data-testid="header-logo-link" aria-label="Vittime del Debito — Torna alla home">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Navigazione principale">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full font-semibold text-base whitespace-nowrap transition-colors duration-200 ${
                  isActive ? "bg-brand-blue text-white" : "text-brand-ink hover:bg-brand-mist"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={waLink("Buongiorno, vorrei informazioni gratuite sui miei diritti.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-button"
            className="ml-2 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white font-bold px-5 h-11 shadow-md shadow-green-500/20 transition-transform duration-200 hover:-translate-y-0.5"
          >
            <MessageCircle className="w-5 h-5" />
            WhatsApp
          </a>
        </nav>

        <button
          className="lg:hidden p-2 rounded-xl hover:bg-brand-mist"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Chiudi menu" : "Apri menu"}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-slate-100 bg-white" aria-label="Menu mobile">
          <div className="container-site py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl font-semibold text-lg ${
                    isActive ? "bg-brand-blue text-white" : "text-brand-ink hover:bg-brand-mist"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={waLink("Buongiorno, vorrei informazioni gratuite sui miei diritti.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-whatsapp-button"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] text-white font-bold px-5 h-12"
            >
              <MessageCircle className="w-5 h-5" />
              Scrivici su WhatsApp
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
